import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  BrandingInfo,
  LoginInfo,
  LogoutInfo,
  MasqueradeInfo,
  StoredTenantInfo,
  TenantInfo,
  UserInfo,
  UserProfile
} from '@models/user-profile.model';
import { SessionStorageService } from '@services/browser-storage/browser-storage.service';

const TENANT_STORAGE_KEY: string = 'ts-tenant-info';
const REDIRECT_URL_STORAGE_KEY: string = 'ts-redirect-url';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private session_id: LoginInfo['session_id'] | undefined = undefined;
  private session_expired: boolean | undefined = undefined;
  private user: UserInfo | undefined = undefined;
  private tenant: TenantInfo | undefined = undefined;
  private branding: BrandingInfo | undefined = undefined;
  private stored_tenant_info: StoredTenantInfo | undefined = undefined;
  private stored_redirect_url: string | undefined = undefined;
  private title_case = new TitleCasePipe();
  private is_masquerading: boolean | undefined = undefined;
  private authorized_to_place_order: boolean = false;
  private authorized_to_ofo: boolean = false;
  private user_profile: UserProfile | undefined = undefined;

  constructor(private storage_svc: SessionStorageService) {}

  isExpired(): boolean | undefined {
    return this.session_expired;
  }

  wasInitialized(): boolean {
    return Boolean(this.getRedirectUrl()) || Object.entries(this.getStoredTenantInfo()).length > 0;
  }

  setSessionId(session_id: LoginInfo['session_id'] | undefined): void {
    this.invalidate();
    this.clearStoredValues();
    this.session_id = session_id;
  }

  initialize(user_profile: UserProfile): void {
    this.clearStoredValues();
    this.user = user_profile.user_profile;
    this.tenant = user_profile.tenant;
    this.session_expired = false;
    this.stored_redirect_url = user_profile.source_base_url;
    this.is_masquerading = (user_profile.masquerader_info as MasqueradeInfo).is_masquerading;
    this.authorized_to_place_order = user_profile.authorized_to_place_order;
    this.authorized_to_ofo = user_profile.authorized_to_order_for_others;
    this.user_profile = user_profile;

    this.storage_svc.setItem<StoredTenantInfo>(TENANT_STORAGE_KEY, {
      id: this.tenant?.id,
      name: this.tenant?.name,
      branding: undefined
    });

    if (user_profile.source_base_url)
      this.storage_svc.setItem<string>(REDIRECT_URL_STORAGE_KEY, user_profile.source_base_url);
  }

  invalidate(info?: LogoutInfo | true): void {
    this.session_id = undefined;
    this.user = undefined;
    this.tenant = undefined;
    this.branding = undefined;
    this.session_expired = info || this.session_expired === false ? true : undefined;

    if (info === true) {
      this.clearStoredValues();
      return;
    }

    if (info?.source_base_url) {
      this.stored_redirect_url = info.source_base_url;
      this.storage_svc.setItem<string>(REDIRECT_URL_STORAGE_KEY, info.source_base_url);
    }
  }

  hasUser(): boolean {
    return (this.user?.id ?? 0) > 0;
  }

  getUserProfile(): UserProfile | undefined {
    return this.user_profile;
  }

  getUser(): UserInfo | undefined {
    return this.user;
  }

  getUserName(): string {
    if (!this.user) return '';

    const name: string | undefined = this.getUserFullName();
    return name ?? (this.user.email ?? '').trim();
  }

  getUserFullName(): string | undefined {
    if (!this.user) return undefined;

    const name: string = `${this.user.first_name} ${this.user.last_name}`.trim();
    return name ? this.title_case.transform(name) : undefined;
  }

  getEmail(strict = false): string {
    if (!this.user) return '';
    if (this.user.email) return this.user.email;
    else if (!strict) return this.user.first_name;
    else return '';
  }

  getUserInitials(): string {
    if (!this.user) return '';

    let initials: string = '';
    if (this.user.first_name) initials += this.user.first_name.charAt(0);
    if (this.user.last_name) initials += this.user.last_name.charAt(0);
    if (!initials && this.user.email) initials = this.user.email.charAt(0);
    return initials.toUpperCase();
  }

  getIsMasquerading(): boolean {
    if (!this.is_masquerading) return false;
    return this.is_masquerading;
  }

  getAuthorizedToPlaceOrder(): boolean {
    return this.authorized_to_place_order;
  }

  getAuthorizedToOfo(): boolean {
    return this.authorized_to_ofo;
  }

  getTenantInfo(): StoredTenantInfo {
    if (this.hasUser())
      return {
        id: this.tenant?.id,
        name: this.tenant?.name,
        branding: this.branding
      };

    return this.getStoredTenantInfo();
  }

  getTenant(): TenantInfo | undefined {
    return this.tenant;
  }

  getBranding(): BrandingInfo | undefined {
    return this.branding;
  }

  setBranding(branding: BrandingInfo): void {
    this.branding = branding;
    this.stored_tenant_info = undefined;

    this.storage_svc.setItem<StoredTenantInfo>(TENANT_STORAGE_KEY, {
      id: this.tenant?.id,
      name: this.tenant?.name,
      branding
    });
  }

  getRedirectUrl(): string | undefined {
    if (!this.stored_redirect_url)
      this.stored_redirect_url = this.storage_svc.getItem<string>(REDIRECT_URL_STORAGE_KEY);

    return this.stored_redirect_url;
  }

  private getStoredTenantInfo(): StoredTenantInfo {
    if (!this.stored_tenant_info)
      this.stored_tenant_info =
        this.storage_svc.getItem<StoredTenantInfo>(TENANT_STORAGE_KEY) ?? ({} as StoredTenantInfo);

    return this.stored_tenant_info;
  }

  private clearStoredValues(): void {
    this.stored_tenant_info = undefined;
    this.stored_redirect_url = undefined;
    this.storage_svc.removeItems(TENANT_STORAGE_KEY);
    this.storage_svc.removeItems(REDIRECT_URL_STORAGE_KEY);
  }
}
